import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonTitle,
    IonToolbar,
    IonSearchbar,
    IonAccordion,
    IonAccordionGroup,
    IonItem,
    IonLabel,
    useIonRouter
} from '@ionic/react';
import { 
    documentTextOutline,
    bookOutline,
    codeSlashOutline,
    peopleOutline,
    shieldCheckmarkOutline,
    analyticsOutline,
    settingsOutline,
    helpCircleOutline,
    downloadOutline,
    linkOutline,
    playCircleOutline,
    bugOutline
} from 'ionicons/icons';
import { useState } from 'react';
import './View.css';

const AdminDocs = () => {
    const router = useIonRouter();
    const [searchText, setSearchText] = useState('');

    const documentationSections = [
        {
            id: 'getting-started',
            title: 'Getting Started',
            icon: playCircleOutline,
            color: 'from-blue-500 to-blue-600',
            description: 'Quick start guide for new administrators',
            articles: [
                {
                    title: 'Admin Panel Overview',
                    description: 'Learn about the main features and layout of the admin panel',
                    content: `
                        <h3>Welcome to the PeoPoll Admin Panel</h3>
                        <p>The admin panel provides comprehensive tools for managing your opinion poll platform. Here's what you can do:</p>
                        <ul>
                            <li><strong>Dashboard:</strong> View key metrics and system status</li>
                            <li><strong>User Management:</strong> Manage user accounts, roles, and permissions</li>
                            <li><strong>Poll Management:</strong> Monitor, moderate, and analyze polls</li>
                            <li><strong>Analytics:</strong> Generate reports and insights</li>
                            <li><strong>Content Moderation:</strong> Review and moderate user-generated content</li>
                            <li><strong>System Settings:</strong> Configure platform settings and preferences</li>
                            <li><strong>AI Assistant:</strong> Smart AI-powered poll generation and optimization</li>
                        </ul>
                        <h4>Navigation</h4>
                        <p>Use the sidebar menu to navigate between sections. Super Admins can filter views using "General Polls", while Org Admins see "My ORG Polls".</p>
                    `
                },
                {
                    title: 'First Time Setup',
                    description: 'Essential steps to configure your platform',
                    content: `
                        <h3>Initial Configuration Steps</h3>
                        <ol>
                            <li><strong>System Settings:</strong> Configure basic site information, branding, and operational parameters</li>
                            <li><strong>User Permissions:</strong> Set up user roles and permissions</li>
                            <li><strong>Content Guidelines:</strong> Establish community guidelines and moderation policies</li>
                            <li><strong>Notifications:</strong> Configure email and notification settings</li>
                            <li><strong>Security:</strong> Set up security measures like rate limiting and IP restrictions</li>
                        </ol>
                        <h4>Recommended Settings</h4>
                        <p>For new installations, we recommend:</p>
                        <ul>
                            <li>Enable email verification for new users</li>
                            <li>Set reasonable poll creation limits</li>
                            <li>Enable spam detection and profanity filtering</li>
                            <li>Configure backup and monitoring systems</li>
                        </ul>
                    `
                }
            ]
        },
        {
            id: 'user-management',
            title: 'User Management',
            icon: peopleOutline,
            color: 'from-green-500 to-green-600',
            description: 'Managing user accounts, roles, and permissions',
            articles: [
                {
                    title: 'User Roles and Permissions',
                    description: 'Understanding different user roles and their capabilities',
                    content: `
                        <h3>User Role Hierarchy</h3>
                        <p>PeoPoll supports multiple user roles with different permission levels:</p>
                        <ul>
                            <li><strong>Super Admin:</strong> Full system access, AI Poll Assistant, and global poll management.</li>
                            <li><strong>Admin:</strong> Manage users, content, and most settings.</li>
                            <li><strong>Moderator:</strong> Content moderation and user support</li>
                            <li><strong>User:</strong> Create polls and participate in voting</li>
                            <li><strong>Guest:</strong> Limited voting access (if enabled)</li>
                        </ul>
                        <h4>Permission Matrix</h4>
                        <table border="1" style="width: 100%; border-collapse: collapse;">
                            <tr><th>Action</th><th>Super Admin</th><th>Admin</th><th>Moderator</th><th>User</th></tr>
                            <tr><td>Manage Users</td><td>✓</td><td>✓</td><td>Limited</td><td>✗</td></tr>
                            <tr><td>System Settings</td><td>✓</td><td>✓</td><td>✗</td><td>✗</td></tr>
                            <tr><td>Content Moderation</td><td>✓</td><td>✓</td><td>✓</td><td>✗</td></tr>
                            <tr><td>Analytics</td><td>✓</td><td>✓</td><td>Limited</td><td>✗</td></tr>
                        </table>
                    `
                },
                {
                    title: 'User Account Management',
                    description: 'How to manage user accounts effectively',
                    content: `
                        <h3>Managing User Accounts</h3>
                        <h4>Creating Users</h4>
                        <p>Admins can create user accounts manually through the user management interface. Required information includes:</p>
                        <ul>
                            <li>Username (must be unique)</li>
                            <li>Email address</li>
                            <li>Initial password</li>
                            <li>User role assignment</li>
                        </ul>
                        <h4>User Actions</h4>
                        <p>Available actions for user accounts:</p>
                        <ul>
                            <li><strong>View:</strong> See user details and activity history</li>
                            <li><strong>Edit:</strong> Modify user information. Now with robust auto-selection for Organization and role fallbacks.</li>
                            <li><strong>Suspend:</strong> Temporarily disable account access</li>
                            <li><strong>Activate:</strong> Re-enable suspended accounts</li>
                            <li><strong>Delete:</strong> Permanently remove account (use with caution)</li>
                        </ul>
                        <h4>Bulk Operations</h4>
                        <p>For managing multiple users simultaneously:</p>
                        <ul>
                            <li>Export user lists</li>
                            <li>Import users from CSV</li>
                            <li>Bulk status changes</li>
                            <li>Mass email notifications</li>
                        </ul>
                    `
                }
            ]
        },
        {
            id: 'content-moderation',
            title: 'Content Moderation',
            icon: shieldCheckmarkOutline,
            color: 'from-orange-500 to-orange-600',
            description: 'Guidelines and tools for content moderation',
            articles: [
                {
                    title: 'Moderation Guidelines',
                    description: 'Best practices for content moderation',
                    content: `
                        <h3>Content Moderation Best Practices</h3>
                        <h4>Review Process</h4>
                        <p>When reviewing reported content:</p>
                        <ol>
                            <li><strong>Assess Context:</strong> Consider the full context of the content</li>
                            <li><strong>Check Guidelines:</strong> Ensure violations align with community guidelines</li>
                            <li><strong>Be Consistent:</strong> Apply rules uniformly across all users</li>
                            <li><strong>Document Decisions:</strong> Always provide clear reasoning for actions</li>
                        </ol>
                        <h4>Common Violations</h4>
                        <ul>
                            <li><strong>Spam:</strong> Repetitive, promotional, or irrelevant content</li>
                            <li><strong>Harassment:</strong> Content targeting individuals or groups</li>
                            <li><strong>Hate Speech:</strong> Content promoting discrimination or violence</li>
                            <li><strong>Inappropriate Content:</strong> Adult content, violence, or disturbing material</li>
                            <li><strong>Copyright:</strong> Content that violates intellectual property rights</li>
                        </ul>
                        <h4>Escalation Process</h4>
                        <p>For complex cases:</p>
                        <ul>
                            <li>Consult with senior moderators</li>
                            <li>Document unusual situations</li>
                            <li>Create precedents for future reference</li>
                            <li>Update guidelines as needed</li>
                        </ul>
                    `
                },
                {
                    title: 'Automated Moderation Tools',
                    description: 'Using system tools to assist with moderation',
                    content: `
                        <h3>Automated Moderation Features</h3>
                        <h4>Spam Detection</h4>
                        <p>The system automatically flags potential spam based on:</p>
                        <ul>
                            <li>Repetitive content patterns</li>
                            <li>Excessive link usage</li>
                            <li>Rapid posting frequency</li>
                            <li>Suspicious user behavior</li>
                        </ul>
                        <h4>Profanity Filter</h4>
                        <p>Automatically filters inappropriate language:</p>
                        <ul>
                            <li>Customizable word lists</li>
                            <li>Context-aware filtering</li>
                            <li>Multiple language support</li>
                            <li>Severity levels</li>
                        </ul>
                        <h4>User Reputation System</h4>
                        <p>Tracks user behavior to assist moderation:</p>
                        <ul>
                            <li>Voting patterns</li>
                            <li>Content quality scores</li>
                            <li>Community feedback</li>
                            <li>Violation history</li>
                        </ul>
                        <h4>Configuring Filters</h4>
                        <p>Adjust automated moderation in System Settings:</p>
                        <ul>
                            <li>Sensitivity levels</li>
                            <li>Action thresholds</li>
                            <li>Whitelist exceptions</li>
                            <li>Notification preferences</li>
                        </ul>
                    `
                }
            ]
        },
        {
            id: 'analytics',
            title: 'Analytics & Reporting',
            icon: analyticsOutline,
            color: 'from-purple-500 to-purple-600',
            description: 'Understanding analytics and generating reports',
            articles: [
                {
                    title: 'Analytics Dashboard',
                    description: 'How to interpret the analytics dashboard',
                    content: `
                        <h3>Understanding Your Analytics</h3>
                        <h4>Key Metrics</h4>
                        <p>The dashboard provides several important metrics:</p>
                        <ul>
                            <li><strong>Total Users:</strong> All registered users on the platform</li>
                            <li><strong>Active Users:</strong> Users who have engaged recently</li>
                            <li><strong>Total Polls:</strong> All polls created on the platform</li>
                            <li><strong>Total Votes:</strong> Sum of all votes cast</li>
                            <li><strong>Engagement Rate:</strong> Percentage of users actively participating</li>
                            <li><strong>Growth Rate:</strong> Platform growth over time</li>
                        </ul>
                        <h4>Trend Analysis</h4>
                        <p>Use trend charts to identify:</p>
                        <ul>
                            <li>Peak usage times</li>
                            <li>Growth patterns</li>
                            <li>Seasonal variations</li>
                            <li>Content performance</li>
                        </ul>
                        <h4>User Demographics</h4>
                        <p>Understand your audience through:</p>
                        <ul>
                            <li>Age group distribution</li>
                            <li>Geographic location data</li>
                            <li>Device usage patterns</li>
                            <li>Engagement preferences</li>
                        </ul>
                    `
                },
                {
                    title: 'Custom Reports',
                    description: 'Creating and exporting custom reports',
                    content: `
                        <h3>Generating Custom Reports</h3>
                        <h4>Report Types</h4>
                        <p>Available report formats:</p>
                        <ul>
                            <li><strong>User Activity Reports:</strong> User engagement and behavior</li>
                            <li><strong>Content Performance:</strong> Poll popularity and engagement</li>
                            <li><strong>Moderation Reports:</strong> Content violations and actions taken</li>
                            <li><strong>System Performance:</strong> Technical metrics and uptime</li>
                        </ul>
                        <h4>Export Options</h4>
                        <p>Reports can be exported in various formats:</p>
                        <ul>
                            <li><strong>PDF:</strong> Formatted reports for presentation</li>
                            <li><strong>CSV:</strong> Raw data for further analysis</li>
                            <li><strong>Excel:</strong> Spreadsheet format with charts</li>
                            <li><strong>JSON:</strong> Machine-readable format for APIs</li>
                        </ul>
                        <h4>Scheduled Reports</h4>
                        <p>Set up automatic report generation:</p>
                        <ul>
                            <li>Daily summaries</li>
                            <li>Weekly analytics</li>
                            <li>Monthly comprehensive reports</li>
                            <li>Custom intervals</li>
                        </ul>
                        <h4>Data Privacy</h4>
                        <p>Ensure compliance when handling user data:</p>
                        <ul>
                            <li>Anonymize personal information</li>
                            <li>Follow data retention policies</li>
                            <li>Respect user privacy settings</li>
                            <li>Comply with local regulations</li>
                        </ul>
                    `
                }
            ]
        },
        {
            id: 'api-reference',
            title: 'API Reference',
            icon: codeSlashOutline,
            color: 'from-indigo-500 to-indigo-600',
            description: 'Technical documentation for developers',
            articles: [
                {
                    title: 'Authentication',
                    description: 'API authentication and security',
                    content: `
                        <h3>API Authentication</h3>
                        <h4>API Keys</h4>
                        <p>To access the PeoPoll API, you need an API key:</p>
                        <pre><code>Authorization: Bearer YOUR_API_KEY</code></pre>
                        <h4>Rate Limiting</h4>
                        <p>API requests are limited to prevent abuse:</p>
                        <ul>
                            <li><strong>Standard:</strong> 1000 requests per hour</li>
                            <li><strong>Premium:</strong> 5000 requests per hour</li>
                            <li><strong>Enterprise:</strong> Custom limits</li>
                        </ul>
                        <h4>Error Codes</h4>
                        <p>Common HTTP status codes:</p>
                        <ul>
                            <li><strong>200:</strong> Success</li>
                            <li><strong>400:</strong> Bad Request</li>
                            <li><strong>401:</strong> Unauthorized</li>
                            <li><strong>403:</strong> Forbidden</li>
                            <li><strong>404:</strong> Not Found</li>
                            <li><strong>429:</strong> Rate Limit Exceeded</li>
                            <li><strong>500:</strong> Internal Server Error</li>
                        </ul>
                    `
                },
                {
                    title: 'Endpoints',
                    description: 'Available API endpoints and their usage',
                    content: `
                        <h3>API Endpoints</h3>
                        <h4>Polls</h4>
                        <ul>
                            <li><code>GET /api/polls</code> - List all polls</li>
                            <li><code>GET /api/polls/{id}</code> - Get specific poll</li>
                            <li><code>POST /api/polls</code> - Create new poll</li>
                            <li><code>PUT /api/polls/{id}</code> - Update poll</li>
                            <li><code>DELETE /api/polls/{id}</code> - Delete poll</li>
                        </ul>
                        <h4>Users</h4>
                        <ul>
                            <li><code>GET /api/users</code> - List users (admin only)</li>
                            <li><code>GET /api/users/{id}</code> - Get user details</li>
                            <li><code>POST /api/users</code> - Create user account</li>
                            <li><code>PUT /api/users/{id}</code> - Update user</li>
                            <li><code>DELETE /api/users/{id}</code> - Delete user</li>
                        </ul>
                        <h4>Votes</h4>
                        <ul>
                            <li><code>POST /api/polls/{id}/vote</code> - Cast vote</li>
                            <li><code>GET /api/polls/{id}/results</code> - Get results</li>
                        </ul>
                        <h4>Analytics</h4>
                        <ul>
                            <li><code>GET /api/analytics/summary</code> - Platform summary</li>
                            <li><code>GET /api/analytics/polls</code> - Poll analytics</li>
                            <li><code>GET /api/analytics/users</code> - User analytics</li>
                        </ul>
                    `
                }
            ]
        },
        {
            id: 'troubleshooting',
            title: 'Troubleshooting',
            icon: bugOutline,
            color: 'from-red-500 to-red-600',
            description: 'Common issues and solutions',
            articles: [
                {
                    title: 'Common Issues',
                    description: 'Solutions to frequently encountered problems',
                    content: `
                        <h3>Common Issues and Solutions</h3>
                        <h4>Login Problems</h4>
                        <p><strong>Issue:</strong> Cannot access admin panel</p>
                        <p><strong>Solutions:</strong></p>
                        <ul>
                            <li>Check username and password</li>
                            <li>Verify account has admin privileges</li>
                            <li>Clear browser cache and cookies</li>
                            <li>Check if two-factor authentication is enabled</li>
                        </ul>
                        
                        <h4>Performance Issues</h4>
                        <p><strong>Issue:</strong> Slow loading times</p>
                        <p><strong>Solutions:</strong></p>
                        <ul>
                            <li>Check server resources (CPU, memory)</li>
                            <li>Optimize database queries</li>
                            <li>Enable caching mechanisms</li>
                            <li>Review network connectivity</li>
                        </ul>
                        
                        <h4>Email Delivery</h4>
                        <p><strong>Issue:</strong> Emails not being sent</p>
                        <p><strong>Solutions:</strong></p>
                        <ul>
                            <li>Verify SMTP configuration</li>
                            <li>Check email service provider settings</li>
                            <li>Review spam folder settings</li>
                            <li>Test with different email providers</li>
                        </ul>
                        
                        <h4>Data Import/Export</h4>
                        <p><strong>Issue:</strong> Problems with data operations</p>
                        <p><strong>Solutions:</strong></p>
                        <ul>
                            <li>Check file format and encoding</li>
                            <li>Verify data structure matches requirements</li>
                            <li>Ensure sufficient server storage</li>
                            <li>Review file size limits</li>
                        </ul>
                    `
                },
                {
                    title: 'System Maintenance',
                    description: 'Regular maintenance tasks and procedures',
                    content: `
                        <h3>System Maintenance</h3>
                        <h4>Regular Tasks</h4>
                        <p>Perform these tasks regularly:</p>
                        <ul>
                            <li><strong>Daily:</strong> Monitor system health, check error logs</li>
                            <li><strong>Weekly:</strong> Review user reports, update content guidelines</li>
                            <li><strong>Monthly:</strong> Database optimization, security updates</li>
                            <li><strong>Quarterly:</strong> Full system backup, performance review</li>
                        </ul>
                        
                        <h4>Database Maintenance</h4>
                        <p>Keep your database healthy:</p>
                        <ul>
                            <li>Regular backups (automated)</li>
                            <li>Index optimization</li>
                            <li>Old data cleanup</li>
                            <li>Performance monitoring</li>
                        </ul>
                        
                        <h4>Security Updates</h4>
                        <p>Stay secure with regular updates:</p>
                        <ul>
                            <li>Apply security patches promptly</li>
                            <li>Update dependencies</li>
                            <li>Review access logs</li>
                            <li>Monitor for suspicious activity</li>
                        </ul>
                        
                        <h4>Performance Optimization</h4>
                        <p>Maintain optimal performance:</p>
                        <ul>
                            <li>Monitor resource usage</li>
                            <li>Optimize slow queries</li>
                            <li>Clean up temporary files</li>
                            <li>Update system configurations</li>
                        </ul>
                    `
                }
            ]
        }
    ];

    const filteredSections = documentationSections.filter(section => 
        searchText === '' || 
        section.title.toLowerCase().includes(searchText.toLowerCase()) ||
        section.description.toLowerCase().includes(searchText.toLowerCase()) ||
        section.articles.some(article => 
            article.title.toLowerCase().includes(searchText.toLowerCase()) ||
            article.description.toLowerCase().includes(searchText.toLowerCase())
        )
    );

    return (
        <IonPage className="view-page">
            <IonHeader className="ion-no-border">
                <IonToolbar className="header-toolbar">
                    <IonButtons slot="start">
                        <IonBackButton text="Back" />
                    </IonButtons>
                    <IonTitle className="page-title">
                        <div className="title-container">
                            <span className="title-text">Documentation</span>
                        </div>
                    </IonTitle>
                    <IonButtons slot="end">
                        <IonButton fill="clear">
                            <IonIcon icon={downloadOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="view-content">
                <div className="polls-container">
                    
                    {/* Welcome Section */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-blue-200">
                        <div className="flex items-center space-x-4 mb-4">
                            <div className="bg-blue-500 rounded-full p-3">
                                <IonIcon icon={bookOutline} className="text-white text-2xl" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">Admin Documentation</h2>
                                <p className="text-gray-600">Comprehensive guides and references for platform administration</p>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-white rounded-lg p-3 text-center">
                                <IonIcon icon={playCircleOutline} className="text-2xl text-blue-600 mb-2" />
                                <div className="text-sm font-medium">Quick Start</div>
                            </div>
                            <div className="bg-white rounded-lg p-3 text-center">
                                <IonIcon icon={codeSlashOutline} className="text-2xl text-green-600 mb-2" />
                                <div className="text-sm font-medium">API Docs</div>
                            </div>
                            <div className="bg-white rounded-lg p-3 text-center">
                                <IonIcon icon={helpCircleOutline} className="text-2xl text-orange-600 mb-2" />
                                <div className="text-sm font-medium">Support</div>
                            </div>
                            <div className="bg-white rounded-lg p-3 text-center">
                                <IonIcon icon={bugOutline} className="text-2xl text-red-600 mb-2" />
                                <div className="text-sm font-medium">Issues</div>
                            </div>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
                        <IonSearchbar
                            value={searchText}
                            onIonInput={(e) => setSearchText(e.detail.value)}
                            placeholder="Search documentation..."
                            className="custom-searchbar"
                        />
                    </div>

                    {/* Documentation Sections */}
                    <div className="space-y-6">
                        {filteredSections.map((section) => (
                            <div key={section.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                                <div className={`bg-gradient-to-r ${section.color} p-6 text-white`}>
                                    <div className="flex items-center space-x-4">
                                        <div className="bg-white bg-opacity-20 rounded-xl p-3">
                                            <IonIcon icon={section.icon} className="text-2xl" />
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold">{section.title}</h3>
                                            <p className="text-sm opacity-90">{section.description}</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="p-6">
                                    <IonAccordionGroup>
                                        {section.articles.map((article, index) => (
                                            <IonAccordion key={index} value={`article-${index}`}>
                                                <IonItem slot="header">
                                                    <IonLabel>
                                                        <h3 className="font-medium text-gray-800">{article.title}</h3>
                                                        <p className="text-sm text-gray-600">{article.description}</p>
                                                    </IonLabel>
                                                </IonItem>
                                                <div slot="content" className="p-4 bg-gray-50">
                                                    <div 
                                                        className="prose max-w-none"
                                                        dangerouslySetInnerHTML={{ __html: article.content }}
                                                    />
                                                </div>
                                            </IonAccordion>
                                        ))}
                                    </IonAccordionGroup>
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredSections.length === 0 && (
                        <div className="text-center py-12">
                            <div className="text-6xl mb-4">📚</div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">No Documentation Found</h3>
                            <p className="text-gray-600">Try different search terms or browse all sections.</p>
                        </div>
                    )}

                    {/* Quick Links */}
                    <div className="bg-gray-50 rounded-xl p-6 mt-8">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Quick Links</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <a href="#" className="flex items-center space-x-2 text-blue-600 hover:text-blue-800">
                                <IonIcon icon={linkOutline} />
                                <span className="text-sm">Video Tutorials</span>
                            </a>
                            <a href="#" className="flex items-center space-x-2 text-blue-600 hover:text-blue-800">
                                <IonIcon icon={linkOutline} />
                                <span className="text-sm">Community Forum</span>
                            </a>
                            <a href="#" className="flex items-center space-x-2 text-blue-600 hover:text-blue-800">
                                <IonIcon icon={linkOutline} />
                                <span className="text-sm">Support Tickets</span>
                            </a>
                            <a href="#" className="flex items-center space-x-2 text-blue-600 hover:text-blue-800">
                                <IonIcon icon={linkOutline} />
                                <span className="text-sm">Release Notes</span>
                            </a>
                        </div>
                    </div>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default AdminDocs;
